import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  ApiError,
  type MatchedLocation,
  type MatchedRequirement,
  type ReviewRequirement,
} from '../../lib/api.js';
import '../../styles/locations.css';
import { useRefusal } from '../../lib/permissions.js';
import { Callout } from '../../components/Callout.js';
import { QueryState } from '../../components/QueryState.js';
import { SearchParametersForm } from '../../components/SearchParametersForm.js';
import { LocationResultRow, RESULTS_PAGE, shortlistedFirst } from '../../components/MatchedLocations.js';
import { LocationDetailPanel } from '../../components/LocationDetailPanel.js';
import { RejectWithReason } from '../../components/RejectWithReason.js';
import { LocationMap } from '../../components/LocationMap.js';
import { ConversationEntry } from '../../components/conversations/ConversationEntry.js';
import { Icon } from '../../components/Icon.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { SplitWorkspace, navigatorKeyDown } from '../../components/ui/SplitWorkspace.js';
import { StatusDot, type WorkState } from '../../components/ui/StatusPill.js';

/**
 * STEP 4 — explore locations, requirement by requirement.
 *
 * The design's shape rather than the workspace tab's: the requirements on the
 * left, the locations found for the selected one on the right. A new
 * composition over the SAME rows — `api.listRequirements`,
 * `api.matchedLocations`, the candidate table behind `setCandidateStatus` —
 * so a location shortlisted here is shortlisted on the Locations tab, and the
 * other way round.
 *
 * TWO RULES THIS SCREEN KEEPS, BOTH RECORDED ELSEWHERE.
 *
 * Approval and sourcing are separate decisions (ADR 0004): `isMatchable` is
 * `APPROVED && ACTIVE`, and nothing here activates sourcing on anybody's
 * behalf. Entering the step runs nothing. A requirement that is approved and
 * not yet searched shows **Start searching**, which activates it and THEN runs
 * matching — explicitly, per requirement, when pressed.
 *
 * And no score. The frozen `96 AI Matching Spec` says never a single number;
 * what is shown is must-have coverage with its denominator and the five axis
 * states with their reasoning, drawn by the same `Coverage` and
 * `WhyItMatches` the Locations tab draws.
 *
 * ZERO RESULTS IS A RESULT. "We searched, and none of the current locations
 * satisfy this requirement" is a true answer, says so, and offers the scouting
 * request beside it. It is not an unfinished step, and finishing setup is
 * never gated on a match existing.
 *
 * ONE RED BUTTON AT A TIME (2026-10-30). The page's is "Finish locations";
 * while the selected requirement is waiting to be searched, "Start searching"
 * is the thing to do and takes it instead. A shortlist on every card was
 * twenty-five red buttons on one page — the rows' actions are neutral, and the
 * rows are the compact ones the Locations tab draws, ten at a time.
 */

type RailState = 'AWAITING_APPROVAL' | 'NEEDS_DETAIL' | 'NOT_SEARCHING' | 'SEARCHED';

function railState(r: ReviewRequirement, searchedIds: ReadonlySet<string>): RailState {
  if (r.status !== 'APPROVED') return 'AWAITING_APPROVAL';
  if (searchedIds.has(r.id)) return 'SEARCHED';
  // Nothing to match on: a title alone would match the whole territory.
  if (!r.hasMatchableSignal) return 'NEEDS_DETAIL';

  return 'NOT_SEARCHING';
}

export function ExploreStep({
  projectId,
  projectTitle,
  onBackToBreakdown,
  onCompare,
  onFinish,
  selectedId: selectedFromAddress = null,
  onSelect,
}: {
  projectId: string;
  projectTitle: string;
  onBackToBreakdown: () => void;
  onCompare: (requirementId: string) => void;
  /** Called AFTER the finish bookmark is recorded. */
  onFinish: () => void;
  /** The selected requirement, when the address owns it (`/explore/:requirementId`). */
  selectedId?: string | null;
  /** Fires when the producer picks a requirement, so the address can follow. */
  onSelect?: (requirementId: string) => void;
}) {
  const queryClient = useQueryClient();
  // The address owns the selection when the route gives one; otherwise the
  // step keeps it itself, so the screen also works without a router.
  const [ownSelectedId, setOwnSelectedId] = useState<string | null>(null);
  const selectedId = selectedFromAddress ?? ownSelectedId;
  const setSelectedId = (id: string) => {
    setOwnSelectedId(id);
    onSelect?.(id);
  };
  const [error, setError] = useState<string | null>(null);
  const [confirmingFinish, setConfirmingFinish] = useState(false);
  /** Narrow screens: a requirement was picked, so its locations show instead of the list. */
  const [picked, setPicked] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);
  const finishRef = useRef<HTMLButtonElement>(null);

  // A question asked below the fold is a button that appears to do nothing.
  // The confirm scrolls into view and takes focus on its safe answer; closing
  // it hands focus back to the button that opened it.
  useEffect(() => {
    if (confirmingFinish) {
      confirmRef.current?.scrollIntoView?.({ block: 'nearest' });
      confirmRef.current?.querySelector<HTMLButtonElement>('[data-testid="explore-finish-stay"]')?.focus();
    }
  }, [confirmingFinish]);

  const parameters = useQuery({
    queryKey: ['search-parameters', projectId],
    queryFn: () => api.searchParameters(projectId),
  });
  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
  });
  const matched = useQuery({
    queryKey: ['matched', projectId],
    queryFn: () => api.matchedLocations(projectId),
  });
  const setup = useQuery({
    queryKey: ['project-setup', projectId],
    queryFn: () => api.projectSetup(projectId),
  });
  // What the breakdown still has open — a producer may arrive here by
  // "Continue anyway" (owner review 2026-10-27, D1), and the notice below
  // keeps that visible rather than letting it read as finished.
  const reviewProgress = useQuery({
    queryKey: ['review-progress', projectId],
    queryFn: () => api.reviewProgress(projectId),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['matched', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['matchable', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
    ]);
  };

  const fail = (e: unknown): void => {
    setError(
      e instanceof ApiError && e.code === 'REQUIREMENT_HAS_NO_MATCHABLE_SIGNAL'
        ? 'Nothing to match on yet — add a location type or a must-have in the breakdown first.'
        : e instanceof ApiError
          ? e.message
          : 'That could not be saved.',
    );
  };

  const save = useMutation({
    mutationFn: (draft: Parameters<typeof api.saveSearchParameters>[1]) =>
      api.saveSearchParameters(projectId, draft),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['search-parameters', projectId] });
    },
    onError: fail,
  });

  /**
   * Activate, THEN search — two calls, in that order, both explicit.
   * Activation is the producer's decision that this requirement is worth a
   * location manager's time; the search is what that decision buys.
   */
  const startSearching = useMutation({
    mutationFn: async (r: ReviewRequirement) => {
      if (r.sourcingStatus !== 'ACTIVE') {
        await api.setSourcing(projectId, r.id, r.rowVersion, 'ACTIVE');
      }

      return api.runMatching(projectId, r.id);
    },
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  // REJECTED takes the reason the API requires; LONGLIST is Restore, the one
  // way back from REJECTED.
  //
  // A refused write re-reads the matched view too: every candidate write is
  // compare-and-swap on `candidateVersion`, and a 409 left the row holding the
  // stale version, so every retry was refused the same way until a reload.
  const decide = useMutation({
    mutationFn: (input: {
      candidateId: string;
      version: number;
      status: 'SHORTLIST' | 'REJECTED' | 'LONGLIST';
      reason?: string;
    }) => api.setCandidateStatus(projectId, input.candidateId, input.version, input.status, input.reason),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: async (e) => {
      fail(e);
      await queryClient.invalidateQueries({ queryKey: ['matched', projectId] });
    },
  });

  const scouting = useMutation({
    mutationFn: (r: { id: string; title: string }) =>
      api.createRequest(projectId, {
        type: 'FIND_LOCATION',
        title: `Find a location: ${r.title}`,
        targets: [{ targetType: 'REQUIREMENT', targetId: r.id }],
      }),
    onSuccess: () => {
      setError(null);
    },
    onError: fail,
  });

  const finish = useMutation({
    mutationFn: () => api.markSetupCompleted(projectId),
    onSuccess: async () => {
      // The Projects list carries the finish bookmark too, so "Continue setup"
      // leaves the row at the same moment it leaves the Overview.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
      ]);
      onFinish();
    },
    onError: fail,
  });

  // WHICH REQUIREMENTS HAVE BEEN SEARCHED is the server's answer, read off the
  // match runs. It was derived here from the matched view, and the matched
  // view lists every approved requirement without a candidate — so on a real
  // production every requirement read "Searched · nothing suitable yet"
  // before anybody had searched anything, and Start searching never showed.
  const searchedIds = useMemo(
    () => new Set<string>(setup.data?.searchedRequirementIds ?? []),
    [setup.data],
  );

  const rail = useMemo(() => {
    const all = requirements.data ?? [];
    // Approved first, in the order the review left them; the rest after, so
    // what can be searched is at the top and what cannot says why below.
    const approved = all.filter((r) => r.status === 'APPROVED');
    const other = all.filter((r) => r.status !== 'APPROVED' && r.status !== 'DISMISSED');

    return [...approved, ...other];
  }, [requirements.data]);

  const selected = rail.find((r) => r.id === selectedId) ?? rail[0] ?? null;

  // The selected requirement's locations, grouped client-side from the
  // one-row-per-source view the workspace uses.
  const locationsFor = useMemo(() => {
    if (!selected) return [];

    return (matched.data?.locations ?? [])
      .map((l) => ({ location: l, hit: l.requirements.find((r) => r.requirementId === selected.id) }))
      .filter((x): x is { location: MatchedLocation; hit: MatchedRequirement } => x.hit !== undefined);
  }, [matched.data, selected]);

  // The server's counts, from the same answer the ids come from; the rail's
  // own arithmetic only while that answer is still arriving.
  const approvedCount = setup.data?.approvedCount ?? rail.filter((r) => r.status === 'APPROVED').length;
  const searchedCount =
    setup.data?.searchedCount ?? rail.filter((r) => r.status === 'APPROVED' && searchedIds.has(r.id)).length;

  if (parameters.isPending || requirements.isPending) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="explore-loading">
        Loading…
      </p>
    );
  }

  if (!parameters.data) {
    return <QueryState query={parameters} subject="the search parameters" testId="explore-parameters" />;
  }

  // Never answered: the same form the Locations tab shows, for the same reason
  // — "never asked" and "asked and skipped" are different states.
  if (parameters.data.current.setAt === null) {
    return (
      <div data-testid="explore-parameters-first">
        {error === null ? null : (
          <p style={{ fontSize: 13, color: 'var(--danger)' }} data-testid="explore-error">
            {error}
          </p>
        )}
        <SearchParametersForm
          current={parameters.data.current}
          catalogue={parameters.data.catalogue}
          recommended={parameters.data.recommended.countries}
          disclaimer={parameters.data.recommended.disclaimer}
          busy={save.isPending}
          onSearch={(draft) => save.mutate(draft)}
          onSkip={() => save.mutate({ countries: [] })}
        />
      </div>
    );
  }

  const selectedState = selected === null ? null : railState(selected, searchedIds);
  // The selected requirement's own next step outranks finishing the workflow.
  const finishIsPrimary = !confirmingFinish && selectedState !== 'NOT_SEARCHING';
  const railIds = rail.map((r) => r.id);
  const note = openBreakdownNote(reviewProgress.data);

  return (
    <div data-testid="explore-step" className="loc-page" style={{ gap: 16 }}>
      <PageHeader
        eyebrow="Locations"
        title="Explore matching locations"
        lede={
          <span data-testid="explore-searched-count">
            {`${String(searchedCount)} of ${String(approvedCount)} requirements searched · ${String(approvedCount - searchedCount)} not searched`}
          </span>
        }
        actions={
          <>
            <button type="button" className="ctl" data-testid="explore-back" onClick={onBackToBreakdown}>
              Back to requirements
            </button>
            <button
              ref={finishRef}
              type="button"
              className={finishIsPrimary ? 'btn btn-primary' : 'btn btn-ghost'}
              data-testid="explore-finish"
              disabled={finish.isPending}
              onClick={() => {
                // Some not searched is information, not a block: searching them is
                // still a decision the producer can take from the workspace.
                if (searchedCount < approvedCount) {
                  setConfirmingFinish(true);

                  return;
                }

                finish.mutate();
              }}
            >
              Finish locations →
            </button>
          </>
        }
      />

      {confirmingFinish ? (
        <div
          className="card1"
          ref={confirmRef}
          role="dialog"
          aria-labelledby="explore-finish-confirm-title"
          data-testid="explore-finish-confirm"
          style={{ padding: 18, maxWidth: '60ch', justifySelf: 'end' }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setConfirmingFinish(false);
              finishRef.current?.focus();
            }
          }}
        >
          <p id="explore-finish-confirm-title" style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>
            {`${String(approvedCount - searchedCount)} approved ${approvedCount - searchedCount === 1 ? 'requirement has' : 'requirements have'} not been searched yet.`}
          </p>
          <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 0', lineHeight: 1.6 }}>
            You can search for them from here or from the workspace at any time.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="ctl"
              data-testid="explore-finish-stay"
              onClick={() => {
                setConfirmingFinish(false);
                finishRef.current?.focus();
              }}
            >
              Stay here
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="explore-finish-anyway"
              disabled={finish.isPending}
              onClick={() => {
                finish.mutate();
              }}
            >
              Finish locations
            </button>
          </div>
        </div>
      ) : null}

      {note === null ? null : (
        <p data-testid="explore-open-breakdown" className="loc-explore-head-note" style={{ margin: 0 }}>
          <span>{note}</span>
          <button type="button" className="ctl" data-testid="explore-open-breakdown-back" onClick={onBackToBreakdown}>
            Finish the breakdown
          </button>
        </p>
      )}

      {error === null ? null : (
        <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }} data-testid="explore-error" role="alert">
          {error}
        </p>
      )}

      <QueryState query={matched} subject="the matches" testId="explore-matched" />

      <SplitWorkspace
        testId="explore-workspace"
        navLabel="Requirements"
        backLabel="Requirements"
        showDetail={selectedFromAddress !== null || picked}
        onBackToList={() => {
          setPicked(false);
        }}
        nav={
          <div data-testid="explore-rail" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <div className="ui-nav-head">
              <p className="loc-nav-title">Requirements</p>
            </div>
            <ol
              className="ui-nav-list"
              onKeyDown={navigatorKeyDown(
                railIds,
                selected?.id ?? null,
                (id) => {
                  setSelectedId(id);
                  setPicked(true);
                },
                (id) => `[data-testid="explore-requirement-${id}"]`,
              )}
            >
              {rail.map((r) => {
                const state = railState(r, searchedIds);
                const on = selected?.id === r.id;
                const count = (matched.data?.locations ?? []).filter((l) =>
                  l.requirements.some((x) => x.requirementId === r.id),
                ).length;
                const words =
                  state === 'AWAITING_APPROVAL'
                    ? 'Awaiting your decision in the breakdown'
                    : state === 'NEEDS_DETAIL'
                      ? 'Needs a location type or a must-have'
                      : state === 'NOT_SEARCHING'
                        ? 'Not searched yet'
                        : count === 0
                          ? 'Searched · nothing suitable yet'
                          : `${String(count)} ${count === 1 ? 'location' : 'locations'}`;

                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="ui-row"
                      data-testid={`explore-requirement-${r.id}`}
                      data-on={on ? '1' : '0'}
                      data-state={state}
                      aria-current={on}
                      onClick={() => {
                        setSelectedId(r.id);
                        setPicked(true);
                      }}
                    >
                      <StatusDot state={RAIL_DOT[state]} label={words} />
                      <span className="ui-row-main">
                        <span className="ui-row-title">{r.title}</span>
                        <span className="ui-row-sub">{words}</span>
                      </span>
                      {state === 'SEARCHED' ? <span className="loc-nav-count">{String(count)}</span> : <span />}
                    </button>
                  </li>
                );
              })}
            </ol>
            {rail.length === 0 ? (
              <p className="mm" style={{ fontSize: 13, margin: 12 }} data-testid="explore-rail-empty">
                No requirements yet.
              </p>
            ) : null}
            <p className="ui-nav-foot">{`The same shortlist ${projectTitle}'s workspace shows.`}</p>
          </div>
        }
        detail={
          <section data-testid="explore-pane" style={{ minWidth: 0 }}>
            {selected === null ? null : (
              <RequirementPane
                projectId={projectId}
                requirement={selected}
                state={railState(selected, searchedIds)}
                locations={locationsFor}
                busy={startSearching.isPending || decide.isPending || scouting.isPending}
                scoutingSent={scouting.isSuccess && scouting.variables?.id === selected.id}
                onBackToBreakdown={onBackToBreakdown}
                onStartSearching={() => startSearching.mutate(selected)}
                onShortlist={(candidateId, version) => decide.mutate({ candidateId, version, status: 'SHORTLIST' })}
                onReject={(candidateId, version, reason) => decide.mutate({ candidateId, version, status: 'REJECTED', reason })}
                onRestore={(candidateId, version) => decide.mutate({ candidateId, version, status: 'LONGLIST' })}
                onCompare={() => {
                  onCompare(selected.id);
                }}
                onRequestScouting={() => scouting.mutate({ id: selected.id, title: selected.title })}
              />
            )}
          </section>
        }
      />
    </div>
  );
}

/** A rail row's state as the workspace's one status vocabulary draws it. */
const RAIL_DOT: Readonly<Record<RailState, WorkState>> = {
  AWAITING_APPROVAL: 'READY_FOR_REVIEW',
  NEEDS_DETAIL: 'NEEDS_ATTENTION',
  NOT_SEARCHING: 'NOT_STARTED',
  SEARCHED: 'COMPLETED',
};

function RequirementPane({
  projectId,
  requirement,
  state,
  locations,
  busy,
  scoutingSent,
  onBackToBreakdown,
  onStartSearching,
  onShortlist,
  onReject,
  onRestore,
  onCompare,
  onRequestScouting,
}: {
  projectId: string;
  requirement: ReviewRequirement;
  state: RailState;
  locations: { location: MatchedLocation; hit: MatchedRequirement }[];
  busy: boolean;
  scoutingSent: boolean;
  onBackToBreakdown: () => void;
  onStartSearching: () => void;
  onShortlist: (candidateId: string, version: number) => void;
  onReject: (candidateId: string, version: number, reason: string) => void;
  onRestore: (candidateId: string, version: number) => void;
  onCompare: () => void;
  onRequestScouting: () => void;
}) {
  const shortlistRefusal = useRefusal('CANDIDATE_EDIT');
  const rejectRefusal = useRefusal('CANDIDATE_REJECT');
  const sourcingRefusal = useRefusal('SOURCING_ACTIVATE');

  /**
   * HOW THE LOCATIONS ARE LOOKED AT (owner review 2026-10-27, points 6, 7, 9
   * and 10): as the list, or on the map; all of them, or only the shortlist —
   * the side-by-side judgement of the places still in the running. Neither is
   * in the URL: they are a way of looking, not a place to come back to.
   */
  const [view, setView] = useState<'LIST' | 'MAP'>('LIST');
  const [shortlistedOnly, setShortlistedOnly] = useState(false);
  const [limit, setLimit] = useState(RESULTS_PAGE);
  /** The location whose detail is open, by its source — or null. */
  const [detailKey, setDetailKey] = useState<string | null>(null);

  // Another requirement starts from its first page.
  useEffect(() => {
    setLimit(RESULTS_PAGE);
  }, [requirement.id, shortlistedOnly]);

  const keyOf = (location: MatchedLocation) => `${location.sourceType}:${String(location.sourceId)}`;
  const visible = useMemo(
    () => (shortlistedOnly ? locations.filter(({ hit }) => hit.candidateStatus === 'SHORTLIST') : locations),
    [locations, shortlistedOnly],
  );
  // The map draws the places still in the running; a rejected one is decided.
  const mapLocations = useMemo(
    () => visible.filter(({ hit }) => hit.candidateStatus !== 'REJECTED').map(({ location }) => location),
    [visible],
  );
  // Stable, because the map re-draws whenever its handler changes identity.
  const openDetail = useCallback((location: MatchedLocation) => {
    setDetailKey(`${location.sourceType}:${String(location.sourceId)}`);
  }, []);
  const detail = detailKey === null ? undefined : locations.find(({ location }) => keyOf(location) === detailKey);

  /** One location row — the same markup open and rejected, and the Locations tab's. */
  const renderRow = ({ location, hit }: (typeof locations)[number]) => {
    const shortlisted = hit.candidateStatus === 'SHORTLIST';
    const isRejected = hit.candidateStatus === 'REJECTED';
    const id = String(location.sourceId);

    return (
      <LocationResultRow
        key={`${location.sourceType}-${id}`}
        testId={`explore-location-${location.sourceType}-${id}`}
        photoPrefix="explore"
        location={location}
        hit={hit}
        hits={[hit]}
        shortlisted={shortlisted}
        badge={
          shortlisted ? (
            <span
              className="ui-pill"
              data-state="CONFIRMED"
              data-size="sm"
              data-testid={`explore-shortlisted-${id}`}
              style={{ marginTop: 6 }}
            >
              Shortlisted
            </span>
          ) : null
        }
        actions={
          <>
            {isRejected ? (
              <button
                type="button"
                className="ctl"
                data-testid={`explore-restore-${id}`}
                disabled={busy || shortlistRefusal !== null}
                title={shortlistRefusal ?? 'Puts this place back among the candidates'}
                onClick={() => {
                  onRestore(hit.candidateId, hit.candidateVersion);
                }}
              >
                Restore
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="ctl"
                  data-testid={`explore-shortlist-${id}`}
                  disabled={busy || shortlisted || shortlistRefusal !== null}
                  title={shortlistRefusal ?? undefined}
                  onClick={() => {
                    onShortlist(hit.candidateId, hit.candidateVersion);
                  }}
                >
                  {shortlisted ? 'Shortlisted' : 'Shortlist'}
                </button>
                <RejectWithReason
                  testId={`explore-reject-${id}`}
                  disabled={busy || rejectRefusal !== null}
                  title={rejectRefusal ?? undefined}
                  onReject={(reason) => {
                    onReject(hit.candidateId, hit.candidateVersion, reason);
                  }}
                />
              </>
            )}
            <button
              type="button"
              className="ctl"
              data-testid={`explore-details-${id}`}
              onClick={() => {
                openDetail(location);
              }}
            >
              {location.sourceType === 'LOCATION' ? 'Photos & details' : 'Details'}
            </button>
            {/* One home per place per production, whatever requirement it is a
                candidate for (ADR 0021). */}
            <ConversationEntry projectId={projectId} anchorType="PLACE" anchorId={id} label={location.title} />
          </>
        }
      />
    );
  };

  const open = shortlistedFirst(
    visible.filter(({ hit }) => hit.candidateStatus !== 'REJECTED'),
    ({ hit }) => hit.candidateStatus === 'SHORTLIST',
  );
  const rejected = visible.filter(({ hit }) => hit.candidateStatus === 'REJECTED');
  const page = open.slice(0, limit);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <h2 className="dh" style={{ fontSize: 24, margin: 0 }}>
            {requirement.title}
          </h2>
          <ConversationEntry projectId={projectId} anchorType="REQUIREMENT" anchorId={requirement.id} label={requirement.title} />
        </div>
        <p className="mm" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
          {requirement.scenes.length > 0
            ? `Scenes ${requirement.scenes.map((s) => s.sceneNumber ?? String(s.ordinal ?? '')).join(', ')}`
            : 'Belongs to the production rather than to a scene'}
        </p>
      </div>

      {state === 'AWAITING_APPROVAL' ? (
        <Callout tone="info" title="Awaiting your decision" testId="explore-awaiting-approval">
          Locations are searched for approved requirements only. Decide on this one in the
          breakdown first.
          <span style={{ display: 'block', marginTop: 8 }}>
            <button type="button" className="ctl" data-testid="explore-go-breakdown" onClick={onBackToBreakdown}>
              Open the breakdown
            </button>
          </span>
        </Callout>
      ) : null}

      {state === 'NEEDS_DETAIL' ? (
        <Callout tone="info" title="Nothing to match on yet" testId="explore-needs-detail">
          A location is matched on its type and on what it must have. This requirement carries
          neither, so a search would return the whole territory and assess none of it. Add a
          location type or a must-have in the breakdown, then search.
          <span style={{ display: 'block', marginTop: 8 }}>
            <button type="button" className="ctl" data-testid="explore-go-breakdown-detail" onClick={onBackToBreakdown}>
              Open the breakdown
            </button>
          </span>
        </Callout>
      ) : null}

      {state === 'NOT_SEARCHING' ? (
        <div className="ui-panel" style={{ padding: 16 }} data-testid="explore-not-searching">
          <p style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>Approved, and not searched yet.</p>
          <p className="mm" style={{ fontSize: 13, lineHeight: 1.6, maxWidth: '58ch', margin: '6px 0 0' }}>
            Searching activates this requirement and runs the match against ScoutAround&rsquo;s
            locations and pins — explicitly, when you say.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="explore-start-searching"
            style={{ marginTop: 12 }}
            disabled={busy || sourcingRefusal !== null}
            title={sourcingRefusal ?? undefined}
            onClick={onStartSearching}
          >
            {busy ? 'Searching…' : 'Start searching'}
          </button>
          {sourcingRefusal ? (
            <p className="mm" style={{ fontSize: 12, marginTop: 8 }} data-testid="explore-sourcing-refusal">
              {sourcingRefusal}
            </p>
          ) : null}
        </div>
      ) : null}

      {state === 'SEARCHED' && locations.length === 0 ? (
        <Callout tone="warn" title="We searched, and none of the current locations satisfy this requirement" testId="explore-zero">
          That is a real answer, not a missing one. A scouting request sends it to a person.
          <span style={{ display: 'block', marginTop: 8 }}>
            {scoutingSent ? (
              <span data-testid="explore-scouting-sent">Scouting request sent.</span>
            ) : (
              <button
                type="button"
                className="ctl"
                data-testid="explore-request-scouting"
                disabled={busy}
                onClick={onRequestScouting}
              >
                Request scouting
              </button>
            )}
          </span>
        </Callout>
      ) : null}

      {locations.length > 0 ? (
        <>
          <div className="loc-toolbar" style={{ marginBottom: 0 }}>
            <p className="mm" style={{ fontSize: 12.5, margin: 0 }} data-testid="explore-location-count">
              {shortlistedOnly
                ? `${String(visible.length)} of ${String(locations.length)} shortlisted`
                : `${String(locations.length)} ${locations.length === 1 ? 'location' : 'locations'}`}
            </p>
            <div className="loc-toggle" role="group" aria-label="Show the locations as">
              {(
                [
                  ['LIST', 'List', 'explore-view-list', 'list'],
                  ['MAP', 'Map', 'explore-view-map', 'map'],
                ] as const
              ).map(([value, label, testId, icon]) => (
                <button
                  key={value}
                  type="button"
                  data-testid={testId}
                  data-on={view === value ? '1' : '0'}
                  aria-pressed={view === value}
                  onClick={() => {
                    setView(value);
                  }}
                >
                  <Icon name={icon} size={14} />
                  {label}
                </button>
              ))}
            </div>
            <label className="loc-check">
              <input
                type="checkbox"
                data-testid="explore-shortlisted-only"
                checked={shortlistedOnly}
                onChange={(e) => {
                  setShortlistedOnly(e.target.checked);
                }}
              />
              Shortlisted only
            </label>
            <div className="loc-toolbar-end">
              <button type="button" className="ctl" data-testid="explore-compare" onClick={onCompare}>
                Compare
              </button>
            </div>
          </div>

          {shortlistedOnly && visible.length === 0 ? (
            <p className="mm" data-testid="explore-shortlisted-empty" style={{ fontSize: 13, margin: 0 }}>
              Nothing is shortlisted for this requirement yet.
            </p>
          ) : null}

          {/* THE TOP VIEW: every place still in the running for this
              requirement, numbered as the map numbers them, a pin opening its
              detail. The Locations tab's own map, not a second one. */}
          {view === 'MAP' ? (
            <div data-testid="explore-map">
              <LocationMap locations={mapLocations} onFocus={openDetail} />
            </div>
          ) : null}

          {view === 'LIST' ? <ul className="loc-rows">{page.map(renderRow)}</ul> : null}

          {view === 'LIST' && open.length > page.length ? (
            <div className="loc-more" style={{ marginTop: 0 }}>
              <button
                type="button"
                className="ctl"
                data-testid="explore-show-more"
                onClick={() => {
                  setLimit(limit + RESULTS_PAGE);
                }}
              >
                {`Show ${String(Math.min(RESULTS_PAGE, open.length - page.length))} more of ${String(open.length - page.length)}`}
              </button>
            </div>
          ) : null}

          {/* REJECTED GOES TO THE BOTTOM, GREYED (owner, 2026-10-30): a place
              ruled out stays in view with its reason, below every place still
              in the running, and Restore puts it back. Nothing is deleted. */}
          {view === 'LIST' && rejected.length > 0 ? (
            <div className="loc-rejected" data-testid="explore-rejected-group">
              <p className="loc-rejected-label">{`Rejected (${String(rejected.length)})`}</p>
              <ul className="loc-rows">{rejected.map(renderRow)}</ul>
            </div>
          ) : null}
        </>
      ) : null}

      {detail === undefined ? null : (
        <LocationDetailPanel
          projectId={projectId}
          location={detail.location}
          hit={detail.hit}
          busy={busy}
          shortlistRefusal={shortlistRefusal}
          rejectRefusal={rejectRefusal}
          onShortlist={() => {
            onShortlist(detail.hit.candidateId, detail.hit.candidateVersion);
          }}
          onReject={(reason) => {
            onReject(detail.hit.candidateId, detail.hit.candidateVersion, reason);
          }}
          onRestore={() => {
            onRestore(detail.hit.candidateId, detail.hit.candidateVersion);
          }}
          onClose={() => {
            setDetailKey(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The breakdown's open business, in one quiet line — or null when there is
 * none. Undecided requirements are NOT searched for (`isMatchable` takes only
 * a confirmed one), and a scene with no requirement has nothing to search for
 * at all; both are worth one line on the screen that does the searching.
 */
export function openBreakdownNote(
  progress: { proposed: number; scenesWithoutRequirements: number; reviewComplete: boolean } | undefined,
): string | null {
  if (progress === undefined || progress.reviewComplete) return null;

  const parts = [
    progress.proposed > 0
      ? `${String(progress.proposed)} requirement${progress.proposed === 1 ? '' : 's'} still need${progress.proposed === 1 ? 's' : ''} a decision and ${progress.proposed === 1 ? 'is' : 'are'} not searched for`
      : null,
    progress.scenesWithoutRequirements > 0
      ? `${String(progress.scenesWithoutRequirements)} scene${progress.scenesWithoutRequirements === 1 ? ' has' : 's have'} no location requirement`
      : null,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? null : `${parts.join(' · ')}.`;
}
